// src/lib/plan.ts

export type Plan = "free" | "premium";
export type Feature = "search" | "analyze" | "export";

export const Limits = {
  free: {
    dailySearch: 5,
    dailyAnalyze: 3,
    dailyExport: 3,
    adzunaCountries: ["us", "gb", "se", "de", "fr", "es", "it", "nl", "ca", "au"],
  },
  premium: {
    dailySearch: 9999,
    dailyAnalyze: 9999,
    dailyExport: 9999,
    adzunaCountries: [
      "us","gb","se","de","fr",
      "ca","au","nz","in","sg",
      "br","za","es","it","nl",
      "pl","mx","ch","at","ie",
      "dk","no","fi","pt","cz",
    ],
  },
} as const;

// ---------- localStorage-based usage ----------

type UsageStore = {
  date: string;       // YYYY-MM-DD
  search: number;
  analyze: number;
  export: number;
};

const USAGE_KEY = "jobtailor_usage_v1";

function todayKey(): string {
  return new Date().toISOString().slice(0, 10); // "2025-12-08"
}

function readUsage(): UsageStore {
  if (typeof window === "undefined") {
    // SSR safety – pretend it's empty
    return { date: todayKey(), search: 0, analyze: 0, export: 0 };
  }

  try {
    const raw = window.localStorage.getItem(USAGE_KEY);
    if (!raw) {
      return { date: todayKey(), search: 0, analyze: 0, export: 0 };
    }
    const parsed = JSON.parse(raw) as UsageStore;

    // new day → reset counts
    if (!parsed.date || parsed.date !== todayKey()) {
      return { date: todayKey(), search: 0, analyze: 0, export: 0 };
    }

    return parsed;
  } catch {
    return { date: todayKey(), search: 0, analyze: 0, export: 0 };
  }
}

function writeUsage(u: UsageStore) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(USAGE_KEY, JSON.stringify(u));
  } catch {
    // ignore quota / private mode errors
  }
}

// Helper type for what Analyzer uses
export type Usage = {
  search: number;
  analyze: number;
  export: number;
};

// For your Analyzer badge
export async function getUsage(): Promise<Usage> {
  const u = readUsage();
  return {
    search: u.search,
    analyze: u.analyze,
    export: u.export,
  };
}

// Increment one counter
export async function track(feature: Feature): Promise<void> {
  const u = readUsage();
  u[feature] = (u[feature] ?? 0) + 1;
  writeUsage(u);
}

// Check limit
export async function canUse(feature: Feature, plan: Plan): Promise<boolean> {
  // Premium: effectively unlimited
  if (plan === "premium") return true;

  const u = readUsage();
  const caps = {
    search: Limits.free.dailySearch,
    analyze: Limits.free.dailyAnalyze,
    export: Limits.free.dailyExport,
  } as const;

  return u[feature] < caps[feature];
}

// Optional – if you ever need to know the plan from backend later:
export async function currentPlan(): Promise<Plan> {
  // For now just return "free" or wire this into Supabase / Stripe later.
  return "free";
}
